import React, { useRef, useEffect, useState } from "react";
import { WhatsAppIcon } from "@/components/icons/WhatsAppIcon";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLocation } from "react-router-dom";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import { ArrowLeft, ArrowRight } from "lucide-react";

const storyItems = [
  {
    type: "image",
    src: "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a331997bc828629fa8100e9.webp",
    position: "object-top",
  },
  {
    type: "text",
    number: "1",
    content:
      "Depois de se aposentar no serviço público, a advogada Valéria Pinheiro decidiu abrir o próprio escritório Pinheiro Advocacia, com a finalidade de atender, exclusivamente, os seus ex-colegas de trabalho.",
  },
  {
    type: "video",
    src: "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a450939d422b180644eae6e.mp4",
    position: "object-top",
  },
  {
    type: "text",
    number: "2",
    content:
      "A ideia surgiu da sua necessidade, enquanto servidora, de encontrar bons profissionais atuando com o direito dos servidores públicos, pois, os poucos que havia no mercado, mal atendiam os clientes, captavam procuração e o contrato eram assinados. Eram anos sem nenhuma notícia.",
  },
  {
    type: "image",
    src: "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a331994bc828629fa80ff51.webp",
    position: "object-left",
  },
  {
    type: "text",
    number: "3",
    content:
      "Para mudar essa realidade a Pinheiro Advocacia priorizou o atendimento humanizado, mantendo um contato direto e permanente com o cliente durante toda a jornada da sua ação judicial. Esse acolhimento diferenciado, aliado à boa técnica empregada nas petições, fez com que em pouco tempo o escritório se tornasse uma referência na área.",
  },
  {
    type: "video",
    src: "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a4503e58b4444aad4d7b2ff.mp4",
  },
  {
    type: "text",
    number: "4",
    content:
      "Já impactamos positivamente a vida de muitos colegas e cuidamos de milhares de ações judiciais graças a utilização de modernos sistemas de dados para gerenciamento de processos. Essa tecnologia nos permitiu crescer, sem renunciar ao nosso maior valor - o atendimento humanizado aos nossos clientes.",
  },
  {
    type: "image",
    src: "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a33199a6ff9a379cdd90fd4.webp",
    bg: true,
    className: "-ml-4 md:-ml-8",
  },
];

export const HorizontalStory = () => {
  const targetRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [scrollRange, setScrollRange] = useState(0);
  const [offsetLeft, setOffsetLeft] = useState(0);

  useEffect(() => {
    const updateLayout = () => {
      if (titleRef.current) {
        setOffsetLeft(titleRef.current.getBoundingClientRect().left);
      }

      setTimeout(() => {
        if (trackRef.current) {
          const track = trackRef.current;
          const secondChild = track.children[1] as HTMLElement;
          // The last actual card is the second to last child (since the last is a spacer)
          const lastCard = track.children[
            track.children.length - 2
          ] as HTMLElement;

          let range = track.scrollWidth - window.innerWidth;

          if (secondChild && lastCard) {
            // Align the last card with the initial position of the second card
            const alignLeftRange = lastCard.offsetLeft - secondChild.offsetLeft;
            range = Math.max(range, alignLeftRange);
          }

          setScrollRange(range > 0 ? range : 0);
        }
      }, 100);
    };

    updateLayout();
    window.addEventListener("resize", updateLayout);

    const timeout = setTimeout(updateLayout, 500);
    return () => {
      window.removeEventListener("resize", updateLayout);
      clearTimeout(timeout);
    };
  }, []);

  const { scrollYProgress } = useScroll({
    target: targetRef,
    offset: ["start start", "end end"],
  });

  const x = useTransform(scrollYProgress, [0, 1], [0, -scrollRange]);

  // Subtle parallax effect for inner content
  const innerX = useTransform(scrollYProgress, [0, 1], [-30, 30]);

  return (
    <section ref={targetRef} className="h-[300vh] relative bg-[#192847]">
      <div className="sticky top-0 h-screen overflow-hidden flex flex-col justify-center pt-16 md:pt-24 lg:pt-32">
        <div className="container mx-auto px-4 md:px-8 mb-0 md:mb-4 lg:mb-10 shrink-0">
          <motion.h2
            ref={titleRef}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
            className="text-3xl md:text-[48px] lg:text-[48px] font-bold text-white leading-tight font-serif mt-0 md:mt-8 lg:mt-0"
          >
            Quem é a Pinheiro - <br />
            Advocacia para Servidores Públicos:
          </motion.h2>
        </div>

        <motion.div
          ref={trackRef}
          style={{ x, paddingLeft: offsetLeft > 0 ? offsetLeft : undefined }}
          className="flex gap-20 md:gap-24 lg:gap-40 pr-4 md:pr-8 w-max items-center mt-4 md:mt-4 lg:mt-10"
        >
          {storyItems.map((item, index) => (
            <div
              key={index}
              className={`flex-shrink-0 relative flex items-center justify-center ${item.type === "text" ? "w-[85vw] md:w-[70vw] lg:w-[32vw] max-w-[500px] md:max-w-[500px] lg:max-w-[500px] 2xl:max-w-[600px]" : index === storyItems.length - 1 ? "w-[70vw] md:w-[45vw] lg:w-[28vw] max-w-[350px] md:max-w-[320px] lg:max-w-[350px] 2xl:max-w-[450px] aspect-[9/16] overflow-hidden rounded-3xl shadow-2xl bg-white border-4 border-white" : item.type === "video" ? "w-[70vw] md:w-[45vw] lg:w-[28vw] max-w-[350px] md:max-w-[320px] lg:max-w-[350px] 2xl:max-w-[450px] aspect-[9/16] overflow-hidden rounded-3xl shadow-2xl bg-white border-4 border-white" : index === 4 ? "w-[85vw] md:w-[80vw] lg:w-[45vw] max-w-[700px] md:max-w-[500px] lg:max-w-[700px] 2xl:max-w-[900px] aspect-[16/9] overflow-hidden rounded-3xl shadow-2xl bg-white border-4 border-white" : "w-[80vw] md:w-[50vw] lg:w-[32vw] max-w-[500px] md:max-w-[400px] lg:max-w-[500px] 2xl:max-w-[600px] aspect-square overflow-hidden rounded-3xl shadow-2xl bg-white border-4 border-white"}`}
            >
              {item.type === "text" ? (
                <div className="w-full flex flex-col justify-center items-center text-center">
                  <div className="w-full mx-auto p-4 md:p-8 flex items-start justify-center pl-0 pr-0">
                    <motion.span
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true, margin: "-50px" }}
                      transition={{ duration: 0.6 }}
                      className="text-5xl md:text-7xl font-serif text-accent font-bold mr-4 md:mr-6 leading-none -mt-1 md:-mt-3"
                    >
                      {item.number}
                    </motion.span>
                    <motion.p
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true, margin: "-50px" }}
                      transition={{ duration: 0.6, delay: 0.2 }}
                      className="text-[16px] md:text-[18px] font-bold leading-relaxed text-white border-l-4 border-accent pl-5 md:pl-6 lg:pl-8 md:pr-4 pr-0 text-left"
                    >
                      {item.content}
                    </motion.p>
                  </div>
                </div>
              ) : item.type === "video" ? (
                <div
                  className={`w-full h-full relative overflow-hidden flex items-center justify-center ${item.bg ? "bg-[#191a1c] p-8 md:p-12" : ""}`}
                >
                  <motion.video
                    style={{
                      x: innerX,
                      scale: 1.15,
                      transformOrigin:
                        item.position === "object-top"
                          ? "top center"
                          : "center",
                    }}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className={`w-full h-full ${item.bg ? "object-contain" : "object-cover"} ${item.position || "object-center"} pointer-events-none`}
                  >
                    <source
                      src={item.src}
                      type={
                        item.src?.endsWith(".mov")
                          ? "video/quicktime"
                          : "video/mp4"
                      }
                    />
                    Seu navegador não suporta a reprodução deste vídeo.
                  </motion.video>
                </div>
              ) : (
                <div
                  className={`w-full h-full relative overflow-hidden flex items-center justify-center ${item.bg ? "bg-[#191a1c] p-8 md:p-12" : ""}`}
                >
                  <motion.img
                    style={{
                      x: innerX,
                      scale: 1.15,
                      transformOrigin:
                        item.position === "object-top"
                          ? "top center"
                          : "center",
                    }}
                    src={item.src}
                    alt={`Story ${index}`}
                    className={`w-full h-full ${item.bg ? "object-contain" : "object-cover"} ${item.position || "object-center"} pointer-events-none ${item.className || ""}`}
                  />
                </div>
              )}
            </div>
          ))}
          {/* Spacer at the end so the last card doesn't hug the screen edge */}
          <div className="w-[4vw] flex-shrink-0" />
        </motion.div>
      </div>
    </section>
  );
};

export const TeamCarousel = ({
  customButton,
}: {
  customButton?: { text: string; onClick: (e: React.MouseEvent) => void };
}) => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const location = useLocation();
  const isProfessoresPage =
    location.pathname === "/areas-de-atuacao/professores-quinquenios";
  const isAposentadosPage =
    location.pathname === "/areas-de-atuacao/aposentados-e-pensionistas";
  const isImpostoDeRendaPage =
    location.pathname === "/areas-de-atuacao/imposto-de-renda";

  const handleContactClick = (e: React.MouseEvent) => {
    if (customButton) {
      customButton.onClick(e);
      return;
    }
    e.preventDefault();
    if (isProfessoresPage) {
      window.dispatchEvent(new CustomEvent("open-professores-popup"));
    } else if (isAposentadosPage) {
      window.dispatchEvent(new CustomEvent("open-aposentados-popup"));
    } else if (isImpostoDeRendaPage) {
      window.dispatchEvent(new CustomEvent("open-imposto-popup"));
    } else {
      window.dispatchEvent(new CustomEvent("open-home-popup"));
    }
  };

  useEffect(() => {
    if (!api) {
      return;
    }

    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  const team = [
    {
      image:
        "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a3356dd0a683b64fecf1d54.webp",
    },
    {
      image:
        "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a3356dd0a683b64fecf1d50.webp",
    },
    {
      image:
        "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a3356dd6dd61c546a163a06.webp",
    },
    {
      image:
        "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a3356db6dd61c546a1639e0.webp",
    },
    {
      image:
        "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a3356db1c5d711b35508f85.webp",
    },
    {
      image:
        "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a3356db5408771d165ae6c6.webp",
    },
    {
      image:
        "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a3356db1c5d711b35508f7b.webp",
    },
    {
      image:
        "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a3356db6a6dd1b69af16255.webp",
    },
    {
      image:
        "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a3356de6dd61c546a163a52.webp",
    },
    {
      image:
        "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a3356de5408771d165ae70f.webp",
    },
    {
      image:
        "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a3356db5408771d165ae6c1.webp",
    },
    {
      image:
        "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a3356dd0a683b64fecf1d4b.webp",
    },
    {
      image:
        "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a3356dd0a683b64fecf1d59.webp",
    },
    {
      image:
        "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a3356dd5408771d165ae6e5.webp",
    },
    {
      image:
        "https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a3356de6dd61c546a163a58.webp",
    },
  ];

  return (
    <>
      <section className="py-24 bg-[#192847] overflow-hidden">
        <div className="container mx-auto px-4">
          {/* Team Grid Section */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-[48px] lg:text-[48px] font-bold text-white mb-6 font-serif">
              Um time inteiro ao seu serviço!
            </h2>
            <div className="w-24 h-1.5 bg-accent mx-auto rounded-full mb-16" />

            <Carousel
              setApi={setApi}
              opts={{ align: "start", loop: true }}
              className="w-full max-w-6xl mx-auto mt-12"
            >
              <CarouselContent className="-ml-4 md:-ml-8">
                {team.map((member, index) => (
                  <CarouselItem
                    key={index}
                    className="pl-4 md:pl-8 basis-full sm:basis-1/2 lg:basis-1/3"
                  >
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true, margin: "-50px" }}
                      transition={{ duration: 0.5, delay: (index % 3) * 0.1 }}
                      className="group text-center"
                    >
                      <div className="relative mx-auto w-full max-w-[350px] aspect-[4/5]">
                        <div className="absolute inset-0 bg-accent rounded-xl transform scale-105 opacity-0 group-hover:opacity-100 transition-all duration-500 blur-sm" />
                        <div className="relative w-full h-full rounded-xl overflow-hidden border-[6px] border-white shadow-xl">
                          <img
                            src={member.image}
                            alt={`Equipe ${index + 1}`}
                            className="w-full h-full object-cover transition-all duration-700 transform group-hover:scale-110"
                            loading="lazy"
                            decoding="async"
                          />
                        </div>
                      </div>
                    </motion.div>
                  </CarouselItem>
                ))}
              </CarouselContent>

              <div className="flex flex-col items-center mt-12 gap-8">
                {/* Pagination Dashes */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.5 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  className="flex justify-center gap-2"
                >
                  {Array.from({ length: count }).map((_, i) => (
                    <button
                      key={i}
                      className={`h-1.5 transition-all duration-300 rounded-full ${i === current ? "w-8 bg-accent" : "w-4 bg-white/30 hover:bg-white/50"}`}
                      onClick={() => api?.scrollTo(i)}
                      aria-label={`Ir para o slide ${i + 1}`}
                    />
                  ))}
                </motion.div>

                {/* Custom Arrows */}
                <div className="flex gap-6 justify-center mt-4">
                  <motion.button
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                    onClick={() => api?.scrollPrev()}
                    className="flex items-center justify-center w-12 h-12 md:w-16 md:h-16 rounded-full border-2 border-white bg-white text-[#192847] hover:bg-transparent hover:text-white transition-colors"
                    aria-label="Slide anterior"
                  >
                    <ArrowLeft className="w-5 h-5 md:w-6 md:h-6" />
                  </motion.button>
                  <motion.button
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                    onClick={() => api?.scrollNext()}
                    className="flex items-center justify-center w-12 h-12 md:w-16 md:h-16 rounded-full border-2 border-white bg-white text-[#192847] hover:bg-transparent hover:text-white transition-colors"
                    aria-label="Próximo slide"
                  >
                    <ArrowRight className="w-5 h-5 md:w-6 md:h-6" />
                  </motion.button>
                </div>
              </div>
            </Carousel>

            <div className="mt-20">
              <a
                href="#contato"
                onClick={handleContactClick}
                className="inline-flex items-center justify-center gap-2 sm:gap-3 whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 w-[85%] sm:w-auto font-bold px-6 py-4 sm:px-8 sm:py-[18px] h-auto rounded-full bg-[#075E54] hover:bg-[#043b35] text-white border-2 border-white shadow-lg hover:scale-105 transition-transform uppercase text-sm sm:text-base mx-auto sm:mx-0"
              >
                <WhatsAppIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                {customButton
                  ? customButton.text
                  : isProfessoresPage
                    ? "Fazer análise gratuita do holerite"
                    : isAposentadosPage
                      ? "Fazer análise gratuita do holerite"
                      : isImpostoDeRendaPage
                        ? "Fazer Checklist do Imposto de Renda"
                        : "Entrar em contato"}
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

const Team = ({
  customButton,
}: {
  customButton?: { text: string; onClick: (e: React.MouseEvent) => void };
}) => (
  <>
    <HorizontalStory />
    <TeamCarousel customButton={customButton} />
  </>
);

export default Team;
